/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

// You can delete this file if you're not using it
import React from "react";
import { VideoPageWrapper, VideoProvider } from "./src/components/context/video";
import { HelmetProvider } from "react-helmet-async";
import { onRouteUpdate as updateScrollOnRouteUpdate } from "./src/hooks/use-keep-scroll";
import { insertChannelsightScript, removeChannelsightScript } from "./src/hooks/use-where-to-buy";
import { productFilterEvent } from "./src/utility/filters-event";
import { UserProvider } from "./src/components/context/user";
import { initCookiePolicy, initPrivacyPolicy } from "./src/utility/onetrust";
import { eventuallyRemoveReevooScript } from "./src/utility/reevoo";
import { hasEcommerce } from "./src/utility/ecommerce";
import { CartPageWrapper, CartProvider } from "./src/components/context/cart";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./src/css/custom.scss";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
    },
  },
});

const attachShadow = HTMLElement.prototype.attachShadow;

HTMLElement.prototype.attachShadow = function (option) {
  const sh = attachShadow.call(this, option);
  //console.info("%s shadow attached to %s", option.mode, this);
  const event = new CustomEvent("attach-shadow", {
    detail: {
      element: this,
    },
  });
  document.dispatchEvent(event);
  //add a MutationObserver here
  return sh;
};

export const wrapRootElement = ({ element }) => (
  <UserProvider>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <VideoProvider>
          {hasEcommerce ? <CartProvider>{element}</CartProvider> : element}
        </VideoProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </UserProvider>
);

export const onRouteUpdate = (params) => {
  updateScrollOnRouteUpdate(params);
  initCookiePolicy();
  initPrivacyPolicy();
  if (params.prevLocation) {
    removeChannelsightScript();
    insertChannelsightScript();
    if (params.location.pathname !== params.prevLocation.pathname) {
      eventuallyRemoveReevooScript();
      const event = new CustomEvent("RouteChange", {
        detail: {
          slug: params.location.pathname,
        },
      });
      document.dispatchEvent(event);
    }
  } else {
    if (params.location.search) {
      const parameters = new URLSearchParams(params.location.search);
      const utmCampaign = parameters.get("utm_campaign");
      const utmMedium = parameters.get("utm_medium");
      const utmSource = parameters.get("utm_source");
      const utmContent = parameters.get("utm_content");
      const utmTerm = parameters.get("utm_term");

      if (utmCampaign) {
        setUtmValue(utmCampaign, "campaign");
      }
      if (utmContent) {
        setUtmValue(utmContent, "content");
      }
      if (utmSource) {
        setUtmValue(utmSource, "source");
      }
      if (utmMedium) {
        setUtmValue(utmMedium, "medium");
      }
      if (utmTerm) {
        setUtmValue(utmTerm, "term");
      }
    }
    productFilterEvent(params);
  }
};

const setUtmValue = (value, label) => {
  const utmStorage = sessionStorage.getItem(`utm_${label}`);
  if (!utmStorage) {
    sessionStorage.setItem(`utm_${label}`, value);
  }
};

export const onInitialClientRender = () => {
  insertChannelsightScript();
  document.addEventListener("reload-channelsight", () => {
    removeChannelsightScript();
    insertChannelsightScript();
  });
};

export const wrapPageElement = ({ element }) => {
  return (
    <VideoPageWrapper>
      {hasEcommerce ? <CartPageWrapper>{element}</CartPageWrapper> : element}
    </VideoPageWrapper>
  );
};
